import { useEffect, useState } from "react";
import { Clock } from "lucide-react";
import WorkDocumentRoomView from "../WorkDocumentRoomView";
import ZoomMeetingDock from "../ZoomMeetingDock";
import SessionTimerTool from "../tools/SessionTimerTool";
import SharedNotesPadTool from "../tools/SharedNotesPadTool";
import { useSessionRoomViewByPlan } from "@/lib/sessionQueries";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATE_LABEL = {
  booked: "Booked", join_window: "Join window open", live: "Live now", ended: "Ended", cancelled: "Cancelled",
};
const AUDIENCE_LABELS = { tools: "Tools", team: "Team", coach: "Coach" };

/**
 * Master v5 Prompt 10 (materials) + Prompt 18A (embedded Zoom) — the
 * Session Room's content, mounted by RoomShell for launch_type='session'
 * exactly like WorkDocumentRoomView is mounted for launch_type=
 * 'work_document' (same shared Room chrome, never a separate shell).
 * The video/call dock (ZoomMeetingDock) and the resource tabs below are
 * deliberately independent components — a Zoom disconnect/leave/error
 * never touches WorkDocument autosave/lease state, and vice versa.
 *
 * Layout fix (user-reported, "Fix Embedded Zoom Frame Sizing + Sticky
 * Session Layout"): this is now a real `h-full flex flex-col` viewport
 * split — a shrink-0 Zoom area at top and a flex-1 overflow-y-auto
 * Resource area below — rather than the previous position:fixed "pin
 * the dock to the viewport, spacer div, capture pinnedTop" hack that
 * ZoomMeetingDock.jsx used to implement. RoomShell.jsx gives this
 * component a real, bounded h-full box (only for the isSession case) so
 * this flex-col's own children can size correctly; see RoomShell's own
 * comment for that half of the fix. Being simply OUTSIDE the one
 * scrolling region is what keeps Zoom visible while a long workbook
 * scrolls — no position:fixed/sticky needed at all anymore.
 * Participant-Specific Session Resource Planning — the Resource area's
 * TOP-LEVEL tabs are now audience groups ("Tools" | "Team" | each other
 * participant, coach-viewer-only | "Coach", coach-viewer-only) straight
 * from view.audience_tabs (already permission-filtered server-side —
 * see build_session_room_view_for_participant); a DROPDOWN inside the
 * active tab picks which of that tab's resources is open. Never one tab
 * per file. Switching either the top-level tab or the dropdown only ever
 * changes the Resource area below — Zoom stays mounted throughout.
 */
export default function SessionRoomRenderer({ workspaceId, srpId }) {
  const { data, isPending, isError } = useSessionRoomViewByPlan(workspaceId, srpId);
  const [activeAudienceKey, setActiveAudienceKey] = useState(null);
  const [selectedByTab, setSelectedByTab] = useState({});

  const audienceTabs = data?.view?.audience_tabs || [];
  const firstTabKey = audienceTabs[0]?.audience_key || null;
  useEffect(() => {
    if (firstTabKey && !activeAudienceKey) setActiveAudienceKey(firstTabKey);
  }, [firstTabKey, activeAudienceKey]);

  if (isError) {
    return <p className="text-terracotta text-sm" data-testid="session-room-error">This Session is no longer available to you.</p>;
  }
  if (isPending || !data) {
    return <p className="text-ink-soft text-sm" data-testid="session-room-loading">Loading your session…</p>;
  }

  const { view, room_state: roomState, session, meeting_available: meetingAvailable, participant_names: participantNames } = data;
  const isCoach = view.viewer_capabilities?.role === "coach";
  const activeTab = audienceTabs.find((t) => t.audience_key === activeAudienceKey) || audienceTabs[0] || null;
  const tabResources = activeTab?.resources || [];
  const activeId = selectedByTab[activeTab?.audience_key] || activeTab?.default_resource_id || tabResources[0]?.resource_id || null;
  const active = tabResources.find((r) => r.resource_id === activeId);

  function tabLabel(key) {
    return AUDIENCE_LABELS[key] || participantNames?.[key] || key;
  }

  return (
    <div className="h-full min-h-0 flex flex-col" data-testid="session-room-view">
      <div className="flex items-center gap-3 mb-3 flex-wrap shrink-0">
        <p className="text-xs uppercase tracking-wide text-ink font-semibold flex items-center gap-1.5">
          <Clock size={13} /> {STATE_LABEL[roomState] || roomState}
        </p>
      </div>

      {/* Zoom area — the source of truth for the Zoom SDK's dimensions;
          the SDK adapts to this box (see ZoomMeetingDock's ResizeObserver),
          this box never grows/shrinks because of the SDK. Deliberately
          shrink-0 and OUTSIDE the scrollable Resource area below — that
          alone keeps it visible while the resource scrolls, no
          position:fixed/sticky trick required. */}
      <div className="shrink-0 w-full max-w-full overflow-hidden">
        <ZoomMeetingDock workspaceId={workspaceId} sesId={session.ses_id} roomState={roomState} meetingAvailable={meetingAvailable} isCoach={isCoach} />
      </div>

      {/* Resource area — the ONLY scrolling region for this Room; a long
          workbook scrolls in here, never growing the whole page. A
          min-h floor (testing_agent, iteration 79, MEDIUM) guarantees
          it's never fully squeezed to 0px even on a very short viewport
          when the Zoom area above is near its own 55vh cap. */}
      <div className="flex-1 min-h-[160px] overflow-y-auto">
        {audienceTabs.length > 0 && (
          <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="session-room-audience-tabs">
            {audienceTabs.map((t) => (
              <button
                key={t.audience_key}
                onClick={() => setActiveAudienceKey(t.audience_key)}
                className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                  t.audience_key === activeTab?.audience_key ? "bg-ink text-paper border-ink" : "border-ink/15 text-ink/60 hover:border-ink/30"
                }`}
                data-testid={`session-room-audience-tab-${t.audience_key}`}
              >
                {tabLabel(t.audience_key)}
              </button>
            ))}
          </div>
        )}

        {tabResources.length > 1 ? (
          <Select value={activeId || ""} onValueChange={(val) => setSelectedByTab((prev) => ({ ...prev, [activeTab.audience_key]: val }))}>
            <SelectTrigger className="h-8 text-sm w-full max-w-xs mb-4" data-testid={`session-room-resource-dropdown-${activeTab?.audience_key}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {tabResources.map((r) => (
                <SelectItem key={r.resource_id} value={r.resource_id} data-testid={`session-room-resource-option-${r.resource_id}`}>
                  {r.resource_id === activeTab?.default_resource_id ? "★ " : ""}{r.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : active ? (
          <p className="text-sm font-medium text-ink mb-3" data-testid={`session-room-resource-title-${active.resource_id}`}>{active.label}</p>
        ) : null}

        {active && active.resource_type === "work_document" ? (
          <WorkDocumentRoomView workspaceId={workspaceId} docId={active.resource_id} isViewAsClient={true} />
        ) : active && active.resource_type === "tool" && active.resource_id === "timer" ? (
          <SessionTimerTool workspaceId={workspaceId} sesId={session.ses_id} isCoach={isCoach} />
        ) : active && active.resource_type === "tool" && active.resource_id === "notes_pad" ? (
          <SharedNotesPadTool workspaceId={workspaceId} sesId={session.ses_id} />
        ) : active && active.resource_type === "tool" ? (
          <p className="text-ink-soft text-sm" data-testid="session-room-tool-unavailable">This tool isn&rsquo;t available yet.</p>
        ) : (
          <p className="text-ink-soft text-sm" data-testid="session-room-no-resources">
            {isCoach ? "You haven't added materials for this session yet." : "Your coach hasn't added materials for this session yet."}
          </p>
        )}
      </div>
    </div>
  );
}
